import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { WSImport } from '../import.models';
import { ImporterService } from '../importer.service';
import { Observable, Subscription } from 'rxjs';
import { PGUtilities } from '../../utilities/pg-utilities';
import { LocalizationService } from '../../services/localization.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';;
import { PgImportPreviewModalComponent } from '../pg-import-preview-modal/pg-import-preview-modal.component';

class PGImportTreeDirectoryProgress {
    page = 0
    pageSize:number = null
    lastPage:number = null
    totalCount:number = null
    error = false;

    isComplete() {
        return this.error || (this.lastPage != null && this.page >= this.lastPage);
    }
}

class PGImportTreeDirectory extends WSImport.TreeDirectory {
    open:boolean = false;
    loading:boolean = false;
    progress = {
        directories: new PGImportTreeDirectoryProgress(),
        files: new PGImportTreeDirectoryProgress(),
    }

    constructor(fromData?:any) {
        super()

        if(fromData != null) {
            for(let i in fromData) {
                if(typeof this[i] != 'undefined' && typeof this[i] != 'function') {
                    this[i] = fromData[i]
                }
            }
        }
    }
}

@Component({
    selector: 'app-pg-import-tree',
    templateUrl: './pg-import-tree.component.html',
    styleUrls: ['./pg-import-tree.component.scss'],
    standalone: false
})
export class PgImportTreeComponent implements OnInit, OnChanges {

    DHMTemplates = {
        'Experience~Event': 8272644,
        'Host': 8272153,
        'Eatery': 8272952,
        'ExperienceSupplier': 8272780,
        'Poi': 8272607,
        'Article': 8272700,
        'Itinerary': 8272728
    }

    DHMTemplatesOptions:Array<{ value: string, text: string }> = [];
    
    DHMTaxonomiesOptions:{ [id:string]: Array<{ value: string, text: string, data?:any }> } = null;

    EPPOIMunicipalityOptions:Array<{ value: string, text: string }> = null;
    EPPOIMunicipalitySelected:string = null;

    searchVal:string = null;

    constructor(private localizationService:LocalizationService, private modalService:NgbModal, private importerService:ImporterService) {
        for(let i in this.DHMTemplates) {
            this.DHMTemplatesOptions.push({ value: this.DHMTemplates[i], text: this.localizationService.translate('RESOURCES.' + i + '.name') })
        }
    }

    @Input() importType:WSImport.JobType;

    @Input() selection:Array<{ id: string, resource: string, municipality?: string, page?: number }>
    @Output() selectionChange = new EventEmitter<Array<{ id: string, resource: string, municipality?: string, page?: number }>>()

    @Input() importedKeys:{ [key:string]: boolean } = {};


    ngOnInit(): void {
        if(this.importType == 'dhm') {
            this.importerService.getCategoriesDHM().subscribe((data) => {
                this.DHMTaxonomiesOptions = data
            })
        }
        else if(this.importType == 'eppoi') {
            this.importerService.getCategoriesEPPOI().subscribe((data) => {
                this.EPPOIMunicipalityOptions = data?.municipality || []
            })
        }
    }

    treeData:{ 
        [id:string]: PGImportTreeDirectory
    } = {};

    ngOnChanges(simpleChanges) {
        if(simpleChanges.importType) {
            this.reset()
        }
    }

    private _loadMoreDataSubscriptions:Array<Subscription> = []
    
    reset() {
        for(let _item of this._loadMoreDataSubscriptions) {
            _item.unsubscribe()
        }

        this._loadMoreDataSubscriptions = []
        this.isSelectingAll = false

        let _root = new PGImportTreeDirectory({ 
            id: '', 
            open: true
        })
        
        if(this.getSearchParams() != null) {
            _root.progress.directories.lastPage = 0
        }
        else {
            _root.progress.files.lastPage = 0
        }

        // Replace the whole map — keeping old child dirs would leave stale files after filter changes
        this.treeData = {
            [_root.id]: _root
        }
        this.loadMoreData(_root.id)
    }

    DHMTemplatesSelected:string = null;
    DHMTaxonomiesProvinceSelected:string = null;
    DHMTaxonomiesCitySelected:string = null;

    onFilterChange() {
        this.reset()
    }

    getEPPOIMunicipalityFilter():string {
        if(this.EPPOIMunicipalitySelected == null || this.EPPOIMunicipalitySelected === '') return null;
        return this.EPPOIMunicipalitySelected;
    }

    getSearchParams() {
        // EPPOI keeps the resource tree; municipality is applied as a tree query param (single-select).
        if(this.importType == 'eppoi') {
            return null;
        }

        if(this.importType == 'dhm') {
            let _templates:Array<string> = PGUtilities.tryParseJSON(this.DHMTemplatesSelected)

            let _taxonomies:Array<string> = []

            let _lists = [
                PGUtilities.tryParseJSON(this.DHMTaxonomiesProvinceSelected), 
                PGUtilities.tryParseJSON(this.DHMTaxonomiesCitySelected)
            ]
            
            for(let _list of _lists) {
                if(_list != null) {
                    for(let _item of _list) {
                        _taxonomies.push(_item)
                    }
                }
            }

            if(
                (_taxonomies != null && _taxonomies.length > 0) ||
                (_templates != null && _templates.length > 0) ||
                (this.searchVal != null && this.searchVal != '')
            ) {
                return { taxonomies: _taxonomies, templates: _templates, search: this.searchVal }
            }
        }
    }

    canLoadMoreData(id:string) {
        let _directory = this.treeData[id];

        for(let i in _directory.progress) {
            if(!_directory.progress[i].isComplete()) return true
        }
        
        return false;
    }

    isSelectingAll = false;

    loadMoreData(id:string, selectAll?:boolean) {
        let _searchParams = this.getSearchParams();

        let _directory = this.treeData[id];

        for(let i in _directory.progress) {
            let _content = i as 'directories'|'files'

            let _progress = _directory.progress[_content]
            if(!_progress.isComplete()) {

                _progress.page++
                _directory.loading = true;

                let _observable:Observable<WSImport.TreePagedDirectory> = null;

                if(_searchParams != null) {
                    _observable = this.importerService.getImportTreePagedSearch(this.importType, _searchParams, _progress.page)
                }
                else {
                    let _treeParams = this.importType == 'eppoi' && this.getEPPOIMunicipalityFilter()
                        ? { municipality: this.getEPPOIMunicipalityFilter() }
                        : undefined
                    _observable = this.importerService.getImportTreePagedDirectory(this.importType, id, _content, _progress.page, _treeParams)
                }

                this._loadMoreDataSubscriptions.push(_observable.subscribe((data) => {
                    _directory.loading = false;

                    if(data == null) {
                        _progress.error = true;
                    }
                    else {
                        _progress.pageSize = data.pageSize
                        _progress.lastPage = data.lastPage
                        _progress.totalCount = data.totalCount
    
                        for(let _item of data.items) {
                            if(_content == 'directories') {
                                let _content = _item as WSImport.TreeDirectoryDirectory
    
                                _directory.directories.push(_content)
    
                                if(this.treeData[_item.id] == null) {
                                    this.treeData[_item.id] = new PGImportTreeDirectory({ id: _item.id })
                                    if(_content.contents.directories == 0) this.treeData[_item.id].progress.directories.lastPage = 0;
                                    if(_content.contents.files == 0) this.treeData[_item.id].progress.files.lastPage = 0;
                                }
                            }
                            else {
                                let _file = Object.assign({}, _item as WSImport.TreeDirectoryFile, { page: _progress.page })
                                _directory.files.push(_file)
                            }
                        }

                        if(selectAll) {
                            if(this.canLoadMoreData(id)) {
                                this.loadMoreData(id, true)
                            }
                            else {
                                this.isSelectingAll = false;
                                this._doSelectAll(id)
                            }
                        }
                    }
                }, () => {
                    _directory.loading = false;
                    _progress.error = true;
                }))

                break;
            }
        }
    }

    toggleDirectoryOpen(child:PGImportTreeDirectory) {
        child.open = !child.open

        if(child.open) {
            this.loadMoreData(child.id)
        }
    }

    isSelected(resource:string, id:string) {
        if(this.selection != null) {
            for(let _item of this.selection) {
                if(_item.id == id && _item.resource == resource) return true;
            }
        }
        return false;
    }

    isAlreadyImported(resource:string, id:string) {
        return this.importedKeys != null && this.importedKeys[resource + '|' + id] === true;
    }

    getEPPOIMunicipality(file?:WSImport.TreeDirectoryFile) {
        return file?.municipality || this.getEPPOIMunicipalityFilter() || null;
    }

    setSelected(resource:string, id:string, val:boolean, directory?:PGImportTreeDirectory, file?:WSImport.TreeDirectoryFile & { page?: number }) {
        if(this.selection) {
            if(val) {
                if(!this.isSelected(resource, id)) {
                    let _item:{ id: string, resource: string, municipality?: string, page?: number } = { resource: resource, id: id }

                    if(this.importType == 'eppoi') {
                        let _municipality = this.getEPPOIMunicipality(file)
                        if(_municipality != null) _item.municipality = _municipality
                        if(file?.page != null) _item.page = file.page
                    }

                    this.selection.push(_item)
                    this.selectionChange.emit(this.selection)
                }
            }
            else {
                for(let i = 0; i < this.selection.length; i++) {
                    if(this.selection[i].id == id && this.selection[i].resource == resource) {
                        this.selection.splice(i, 1)
                        this.selectionChange.emit(this.selection)
                        break;
                    }
                }
            }
        }
    }

    openPreview(resource:string, id:string, directory?:PGImportTreeDirectory, file?:WSImport.TreeDirectoryFile & { page?: number }) {
        let _modalRef = this.modalService.open(PgImportPreviewModalComponent, { size: 'xl' })
        let _componentInstance = _modalRef.componentInstance as PgImportPreviewModalComponent;

        _componentInstance.importType = this.importType;
        _componentInstance.resourceName = resource;
        _componentInstance.elementId = id;

        if(this.importType == 'eppoi') {
            _componentInstance.municipality = this.getEPPOIMunicipality(file)
            if(file?.page != null) _componentInstance.page = file.page
        }
    }

    isAllSelected(id:string) {
        if(this.canLoadMoreData(id)) {
            return false
        }
        else {
            let _directory = this.treeData[id];

            for(let _item of _directory.files) {
                if(_item.resource && !this.isSelected(_item.resource, _item.id)) return false;
            }

            return true;
        }
    }

    toggleSelectAll(id:string) {
        if(this.isAllSelected(id)) {
            let _directory = this.treeData[id];

            for(let _item of _directory.files) {
                for(let i = 0; i < this.selection.length; i++) {
                    if(this.selection[i].id == _item.id && this.selection[i].resource == _item.resource) {
                        this.selection.splice(i, 1)
    
                        break;
                    }
                }
            }
            this.selectionChange.emit(this.selection)
        }
        else {
            if(this.canLoadMoreData(id)) {
                this.isSelectingAll = true;
                this.loadMoreData(id, true)
            }
            else {
                this._doSelectAll(id)
            }
        }
    }

    private _doSelectAll(id:string) {
        let _directory = this.treeData[id];

        for(let _item of _directory.files) {
            if(_item.resource && !this.isSelected(_item.resource, _item.id)) {
                let _selectionItem:{ id: string, resource: string, municipality?: string, page?: number } = {
                    resource: _item.resource,
                    id: _item.id
                }

                if(this.importType == 'eppoi') {
                    let _municipality = this.getEPPOIMunicipality(_item)
                    if(_municipality != null) _selectionItem.municipality = _municipality
                    if((_item as any).page != null) _selectionItem.page = (_item as any).page
                }

                this.selection.push(_selectionItem)
            }
        }
        
        this.selectionChange.emit(this.selection)
    }
}
